import React, { useState, useRef, useEffect } from "react"
import { IStateConfig } from "../types"
import {
  DropdownItem,
  DropdownMenu,
  DropdownWrapper,
  ErrorTooltip,
} from "./styled"
import CircleX from "../../../assets/circle-x.svg"
import ChevronUp from "../../../assets/chevron-up.svg"
import ChevronDown from "../../../assets/chevron-down.svg"
import { colors } from "../../../../themes/colors"
import Triangle from "../../../assets/triangle.svg"

interface IStateDropdownProps {
  options: IStateConfig[]
  onSelect: (option: IStateConfig | null) => void
  LeftIcon: ({ fill }: { fill: string }) => JSX.Element
  value: IStateConfig | null
  error?: string
  isDisabled?: boolean
  placeholder?: string
}

export const StateDropdown = ({
  options,
  onSelect,
  LeftIcon,
  value,
  error,
  isDisabled = false,
  placeholder,
}: IStateDropdownProps) => {
  const [text, setText] = useState<string>(value ? value.name : "")
  const [isOpen, setIsOpen] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(-1)
  const [isFocused, setIsFocused] = useState(false)
  const [filteredOptions, setFilteredOptions] =
    useState<IStateConfig[]>(options)
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const optionsRefs = useRef<Array<HTMLDivElement | null>>([])

  useEffect(() => {
    setFilteredOptions(options)
  }, [options])

  useEffect(() => {
    setText(value ? value.name : "")
  }, [value])

  const toggleDropdown = () => {
    if (isDisabled) return
    inputRef.current?.focus()
    setIsFocused(true)
    setIsOpen(!isOpen)
  }

  const closeDropdown = () => {
    setIsFocused(false)
    setText(value ? value.name : "")
    setIsOpen(false)
  }

  const handleInputChange = (value: string) => {
    setText(value)
    setFilteredOptions(
      options.filter(option =>
        option.name.toLowerCase().includes(value.toLowerCase())
      )
    )
    if (!isOpen) setIsOpen(true)
  }

  const handleSelectOption = (option: IStateConfig) => {
    onSelect(option)
    setIsOpen(false)
    setText(option.name)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (filteredOptions.length < 1) return

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        setCurrentIndex(prevIndex => (prevIndex + 1) % filteredOptions.length)
        break
      case "ArrowUp":
        event.preventDefault()
        setCurrentIndex(
          prevIndex =>
            (prevIndex - 1 + filteredOptions.length) % filteredOptions.length
        )
        break
      case "Enter":
        event.preventDefault()
        if (currentIndex >= 0 && currentIndex < filteredOptions.length) {
          handleSelectOption(filteredOptions[currentIndex])
        }
        break
      default:
        break
    }
  }

  const handleXClick = () => {
    onSelect(null)
    setText("")
    handleInputChange("")
  }

  return (
    <DropdownWrapper
      ref={dropdownRef}
      onClick={toggleDropdown}
      onBlur={closeDropdown}
      onKeyDown={handleKeyDown}
      isFocused={isFocused}
      error={!!error}
      isHaveLeftRadius
      data-testid="state-dropdown"
    >
      <LeftIcon
        fill={isDisabled ? colors.c_grey[500] : colors.c_primary[600]}
      />{" "}
      <input
        ref={inputRef}
        value={text}
        placeholder={placeholder ?? "Select a state"}
        onChange={e => handleInputChange(e.currentTarget.value)}
        type="text"
        disabled={isDisabled}
      />
      {value ? (
        <img src={CircleX} alt="" onClick={handleXClick} />
      ) : (
        <img src={isOpen ? ChevronUp : ChevronDown} alt="" />
      )}
      {isOpen && (
        <DropdownMenu>
          {filteredOptions.map((option, index) => (
            <DropdownItem
              key={option.region}
              ref={el => (optionsRefs.current[index] = el)}
              selected={index === currentIndex}
              onMouseDown={() => handleSelectOption(option)}
              onMouseOver={() => setCurrentIndex(index)}
            >
              {option.name}
              {value?.name === option.name && " (selected)"}
            </DropdownItem>
          ))}
        </DropdownMenu>
      )}
      {error && !isOpen && (
        <ErrorTooltip>
          <img src={Triangle} alt="" />
          <p>{error}</p>
        </ErrorTooltip>
      )}
    </DropdownWrapper>
  )
}
